import React, { useEffect, useMemo, useState } from "react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { LogService } from "../../../../services/LogService";
import { StockService } from "../../../../services/StockService";

// A species' market value is treated as a rate per m³, so its cost scales with the volume dispatched.
const costOf = (volume, rate) => volume * rate;

const formatVolume = (v) =>
  `${v.toLocaleString("en-GH", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³`;
const formatMoney = (v) =>
  `GHS ${v.toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const useDispatchBreakdown = (treeFellIds) => {
  const [treesById, setTreesById] = useState({});
  const [speciesById, setSpeciesById] = useState({});
  const [lookupsReady, setLookupsReady] = useState(false);
  const [fellItems, setFellItems] = useState({});

  useEffect(() => {
    Promise.all([StockService.GetTrees(), StockService.GetAllSpecies()]).then(([trees, species]) => {
      const t = {};
      (trees || []).forEach((tree) => {
        t[tree.treeId] = tree;
      });
      const s = {};
      (species || []).forEach((sp) => {
        s[sp.speciesId] = sp;
      });
      setTreesById(t);
      setSpeciesById(s);
      setLookupsReady(true);
    });
  }, []);

  useEffect(() => {
    treeFellIds
      .filter((id) => !(id in fellItems))
      .forEach((id) => {
        setFellItems((m) => ({ ...m, [id]: { status: "loading", items: [] } }));
        LogService.GetTreeLogDetail(id).then((res) => {
          const ok = res && !Array.isArray(res);
          setFellItems((m) => ({
            ...m,
            [id]: { status: ok ? "ready" : "error", items: ok ? res.fellItems || [] : [] },
          }));
        });
      });
    // fellItems is a cache keyed by id; re-running on its changes would only find nothing new to fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeFellIds]);

  return useMemo(() => {
    const selected = treeFellIds.map((id) => fellItems[id]).filter(Boolean);
    const loading = !lookupsReady || selected.some((f) => f.status === "loading");
    const failed = selected.filter((f) => f.status === "error").length;

    const bySpecies = new Map();
    selected
      .filter((f) => f.status === "ready")
      .forEach((f) =>
        f.items.forEach((item) => {
          const speciesId = item.speciesId || treesById[item.treeId]?.speciesId || 0;
          const row = bySpecies.get(speciesId) || { speciesId, volume: 0, pieces: 0 };
          row.volume += Number(item.volume) || 0;
          row.pieces += 1;
          bySpecies.set(speciesId, row);
        })
      );

    const rows = [...bySpecies.values()]
      .map((row) => {
        const species = speciesById[row.speciesId] || {};
        const price = Number(species.marketValue) || 0;
        return {
          ...row,
          name: species.speciesName || "Unknown species",
          code: species.speciesCode,
          price,
          cost: price > 0 ? costOf(row.volume, price) : null,
        };
      })
      .sort((a, b) => b.volume - a.volume);

    return {
      rows,
      loading,
      failed,
      totalVolume: rows.reduce((s, r) => s + r.volume, 0),
      totalCost: rows.reduce((s, r) => s + (r.cost || 0), 0),
      unpriced: rows.filter((r) => r.cost === null).length,
    };
  }, [treeFellIds, fellItems, treesById, speciesById, lookupsReady]);
};

const DispatchBreakdown = ({ breakdown, hasLogs }) => {
  const { rows, loading, failed, totalVolume, totalCost, unpriced } = breakdown;

  return (
    <div className="p-5 bg-white border shadow-sm rounded-2xl border-slate-200/80">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold tracking-widest uppercase text-slate-400">Volume &amp; cost</p>
        {loading && hasLogs && (
          <span className="w-3.5 h-3.5 border-2 rounded-full animate-spin border-slate-200 border-t-tclPrimary" />
        )}
      </div>

      {!hasLogs ? (
        <p className="mt-4 text-sm text-slate-300">Select a log to see volume and cost per species.</p>
      ) : rows.length === 0 ? (
        <p className={`mt-4 text-sm ${!loading && failed > 0 ? "text-rose-600" : "text-slate-400"}`}>
          {loading
            ? "Loading log details…"
            : failed > 0
            ? `Couldn't load ${failed === 1 ? "the selected log" : `${failed} of the selected logs`}. Try again later.`
            : "The selected log has no pieces recorded."}
        </p>
      ) : (
        <>
          <ul className="mt-3 divide-y divide-slate-100">
            {rows.map((r) => (
              <li key={r.speciesId} className="py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 font-medium break-words text-slate-800">
                    {r.name}
                  </span>
                  <span className="text-sm font-semibold whitespace-nowrap tabular-nums text-slate-800">
                    {r.cost === null ? (
                      <span className="font-normal text-slate-300" title="No market value set for this species">No price</span>
                    ) : (
                      formatMoney(r.cost)
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-1 text-xs text-slate-500">
                  {r.code && (
                    <span className="rounded-md bg-tclPrimary/10 px-1.5 py-px font-mono text-[11px] font-semibold text-tclPrimary">
                      {r.code}
                    </span>
                  )}
                  <span className="tabular-nums">{formatVolume(r.volume)}</span>
                  {r.price > 0 && (
                    <span className="tabular-nums text-slate-400">× {formatMoney(r.price)}/m³</span>
                  )}
                  <span className="text-slate-400">
                    · {r.pieces} {r.pieces === 1 ? "piece" : "pieces"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <dl className="pt-3 mt-1 space-y-2 border-t-2 border-slate-200">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-sm text-slate-500">Total volume</dt>
              <dd className="text-sm font-semibold whitespace-nowrap tabular-nums text-slate-800">
                {formatVolume(totalVolume)}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-sm text-slate-500">Total cost</dt>
              <dd className="text-lg font-bold whitespace-nowrap tabular-nums text-tclPrimary">
                {formatMoney(totalCost)}
              </dd>
            </div>
          </dl>
          {(unpriced > 0 || failed > 0) && (
            <div className="mt-4 space-y-1">
              {unpriced > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-amber-600">
                  <HiOutlineExclamationCircle className="w-3.5 h-3.5 mt-px shrink-0" />
                  {unpriced} {unpriced === 1 ? "species has" : "species have"} no market value set, so the total cost leaves {unpriced === 1 ? "it" : "them"} out.
                </p>
              )}
              {failed > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-rose-600">
                  <HiOutlineExclamationCircle className="w-3.5 h-3.5 mt-px shrink-0" />
                  Couldn't load {failed} of the selected logs, so the totals are incomplete.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DispatchBreakdown;
